import {
  CALENDAR_DAY_QUERY,
  calendarClockOf,
  formatCalendarMonths,
  initialCalendarDay,
  initialCalendarWeek,
  placePosts,
  postsByCell,
  weekDaysOf
} from 'utils/socialCampaign/calendar'
import { CampaignPhase, SocialCampaignDTO } from 'models/socialCampaign'
import { ChevronLeftOutlined, ChevronRightOutlined } from '@mui/icons-material'
import { FC, useMemo, useState } from 'react'
import { addDays, mondayOf } from 'utils/socialCampaign/schedule'

import { CampaignCalendarDay } from './CampaignCalendarDay.component'
import { CampaignCalendarWeek } from './CampaignCalendarWeek.component'
import { MUIButton } from 'components/common/MUIButton'
import Typography from '@mui/material/Typography'
import styles from './CampaignCalendar.module.sass'
import { useCalendarPostSummary } from './CalendarPost.component'
import { useMatchMedia } from './useMatchMedia'
import { useTranslation } from 'react-i18next'

/** A day and an hour of the campaign's timezone: `YYYY-MM-DD` and `HH:00` */
export interface CalendarSlot {
  date: string
  time: string
}

interface Props {
  campaign: SocialCampaignDTO
  now: Date
  /** Opens a post in the editor; the posts are static content without it */
  onOpenPost?: (postId: string) => void
  /** A free hour opens the editor in Create mode on that day and hour; no free hour is offered without it */
  onAddPostAt?: (slot: CalendarSlot) => void
}

/**
 * The schedule's week calendar (ISSUE-3649, prototype v3 `calendarHtml`, v1's layout): a bordered frame, "Today | ‹ › |
 * October 2026", then the week's hour grid from 861px or, below, the strip of its days over the picked day hour by
 * hour. Times in the campaign's timezone. Opens on the week of the first post still to go out, else this week. A free
 * hour creates a post there, except on an ended campaign. One of the two views is rendered, not both hidden by CSS:
 * every post and free hour is in the page once. Not built: month view, status filter, drag (the editor moves a post).
 */
export const CampaignCalendar: FC<Props> = ({ campaign, now, onOpenPost, onAddPostAt }) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const dayView = useMatchMedia(CALENDAR_DAY_QUERY)
  const clock = calendarClockOf(now, campaign.timezone, campaign.editFreezeMinutes)
  const placements = useMemo(() => placePosts(campaign.posts, campaign.timezone), [campaign.posts, campaign.timezone])
  const cells = useMemo(() => postsByCell(placements), [placements])
  const summaryOf = useCalendarPostSummary(campaign, now)

  const [week, setWeek] = useState(() => initialCalendarWeek(campaign, now))
  const [day, setDay] = useState(() => initialCalendarDay(week, placements, clock))
  // Today scrolls back even when it shows the week and day already there (prototype `cal-today` re-renders)
  const [scrollRequest, setScrollRequest] = useState(0)
  const days = weekDaysOf(week)
  const { months, year } = formatCalendarMonths(week, i18n.language)
  const addPostAt = campaign.phase !== CampaignPhase.ENDED ? onAddPostAt : undefined

  const showWeek = (monday: string, picked?: string) => {
    setWeek(monday)
    setDay(picked ?? initialCalendarDay(monday, placements, clock))
    setScrollRequest((request) => request + 1)
  }

  return (
    <div className={styles.calendar} data-layout={dayView ? 'day' : 'week'}>

      <div className={styles.bar}>

        <MUIButton type="secondaryBorder" size="sm" onClick={() => showWeek(mondayOf(clock.today), clock.today)}>{t('schedule.calendar.today')}</MUIButton>

        <span className={styles.sep} aria-hidden="true" />

        <span className={styles.nav}>

          <MUIButton type="secondaryBorder" size="sm" iconOnly aria-label={t('schedule.calendar.previous_week')} onClick={() => showWeek(addDays(week, -7))}>
            <ChevronLeftOutlined sx={{ fontSize: '2rem' }} />
          </MUIButton>

          <MUIButton type="secondaryBorder" size="sm" iconOnly aria-label={t('schedule.calendar.next_week')} onClick={() => showWeek(addDays(week, 7))}>
            <ChevronRightOutlined sx={{ fontSize: '2rem' }} />
          </MUIButton>

        </span>

        <Typography variant="text-md" component="p" className={styles.label} aria-live="polite"><strong>{months}</strong> <span>{year}</span></Typography>

      </div>

      {dayView &&
        <CampaignCalendarDay
          days={days}
          day={day}
          onDayChange={setDay}
          scrollRequest={scrollRequest}
          placements={placements}
          cells={cells}
          clock={clock}
          summaryOf={summaryOf}
          onOpenPost={onOpenPost}
          onAddPostAt={addPostAt}
        />
      }

      {!dayView &&
        <CampaignCalendarWeek
          days={days}
          scrollRequest={scrollRequest}
          placements={placements}
          cells={cells}
          clock={clock}
          summaryOf={summaryOf}
          onOpenPost={onOpenPost}
          onAddPostAt={addPostAt}
        />
      }

    </div>
  )
}
